/**
 * Lueur LED orange diffuse sur les 4 bords du viewport, suivant le curseur.
 *
 * Overlay purement décoratif : `position: fixed` + `pointer-events: none` +
 * `mix-blend-mode: screen` (ajoute de la lumière, n'assombrit jamais). Il
 * n'obstrue donc aucun élément et n'intercepte aucune interaction. Tout le
 * rendu vit dans `assets/valise-premium.css` ; ce composant se contente de
 * mettre à jour deux variables CSS (`--gx`, `--gy`, en % du viewport) avec un
 * suivi lissé (lerp). La boucle d'animation s'arrête dès la cible atteinte.
 *
 * @element cursor-glow
 */
class CursorGlow extends HTMLElement {
  /**
   * Position cible (curseur) en % du viewport.
   * @type {number}
   */
  #targetX = 50;

  /** @type {number} */
  #targetY = 50;

  /**
   * Position courante lissée en % du viewport.
   * @type {number}
   */
  #currentX = 50;

  /** @type {number} */
  #currentY = 50;

  /**
   * Identifiant de la frame d'animation en cours (0 = aucune).
   * @type {number}
   */
  #frame = 0;

  connectedCallback() {
    // Sans curseur fin (tactile) ou en mouvement réduit : on garde le halo
    // statique défini en CSS, sans suivi ni écouteur.
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!finePointer.matches || reducedMotion.matches) return;

    window.addEventListener('pointermove', this.#handlePointerMove, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('pointermove', this.#handlePointerMove);
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  /**
   * Mémorise la cible et (ré)amorce la boucle de rendu.
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    this.#targetX = (event.clientX / window.innerWidth) * 100;
    this.#targetY = (event.clientY / window.innerHeight) * 100;
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#render);
  };

  /**
   * Rapproche la position courante de la cible (lerp), écrit les variables CSS,
   * puis reprogramme une frame tant que la cible n'est pas atteinte.
   */
  #render = () => {
    const ease = 0.14;
    this.#currentX += (this.#targetX - this.#currentX) * ease;
    this.#currentY += (this.#targetY - this.#currentY) * ease;

    this.style.setProperty('--gx', `${this.#currentX.toFixed(2)}%`);
    this.style.setProperty('--gy', `${this.#currentY.toFixed(2)}%`);

    const settled =
      Math.abs(this.#targetX - this.#currentX) < 0.1 &&
      Math.abs(this.#targetY - this.#currentY) < 0.1;

    this.#frame = settled ? 0 : requestAnimationFrame(this.#render);
  };
}

if (!customElements.get('cursor-glow')) {
  customElements.define('cursor-glow', CursorGlow);
}
